/**
 * ContractForm page — create/edit contract with all fields, multi-select
 * budget categories, site association picker, GL code input, and
 * AI-assisted document upload for auto-populating fields on create.
 *
 * Requirements: 3.1, 3.5, 3.6, 3.7, 19.1, 19.2, 19.3, 19.4
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  contractService,
  type CreateContractInput,
  type UpdateContractInput,
} from '@/services/contractService';
import { vendorService } from '@/services/vendorService';
import { FormField } from '@/components/ui/FormField';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { AIDocumentUpload } from '@/components/AIDocumentUpload';
import { ConfidenceField } from '@/components/shared/ConfidenceField';
import { useConfidenceConfirmation } from '@/hooks/useConfidenceConfirmation';
import type { ContractSetupResult } from '@/services/aiSetupService';
import type { Vendor } from '@/types';

/** Tracks which fields were populated by AI and their confidence scores. */
interface AIFieldMeta {
  fieldName: string;
  confidence: number;
}

export function ContractForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEdit = !!editId;

  const [form, setForm] = useState({
    vendorId: '',
    contractTitle: '',
    contractType: '',
    effectiveDate: '',
    expirationDate: '',
    autoRenewalTerms: '',
    cancellationNoticePeriodDays: '',
    contractValue: '',
    paymentTerms: '',
    budgetCategory: '',
    glCode: '',
    departmentScope: '',
    notes: '',
    erpPoNumber: '',
    erpGlCode: '',
  });

  const [budgetCategories, setBudgetCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [siteNumbers, setSiteNumbers] = useState<string[]>([]);
  const [newSite, setNewSite] = useState('');
  const [annualCosts, setAnnualCosts] = useState<Array<{ fiscalYear: string; annualAmount: string; notes: string }>>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // AI-assisted setup state
  const [aiResult, setAiResult] = useState<ContractSetupResult | null>(null);
  const [aiFieldMeta, setAiFieldMeta] = useState<AIFieldMeta[]>([]);
  const [aiAssistedCreation, setAiAssistedCreation] = useState(false);
  const confidence = useConfidenceConfirmation();

  // Load existing contract for edit mode
  const { data: existingData, isLoading: loadingContract } = useQuery({
    queryKey: ['contract', Number(editId)],
    queryFn: () => contractService.getById(Number(editId)),
    enabled: isEdit,
  });

  // Load vendors for the dropdown
  const { data: vendorsData } = useQuery({
    queryKey: ['vendors', { pageSize: 200, isActive: true }],
    queryFn: () => vendorService.list({ pageSize: 200, isActive: true }),
  });

  useEffect(() => {
    if (existingData && isEdit) {
      const c = existingData.contract;
      setForm({
        vendorId: String(c.vendorId),
        contractTitle: c.contractTitle,
        contractType: c.contractType ?? '',
        effectiveDate: c.effectiveDate ?? '',
        expirationDate: c.expirationDate ?? '',
        autoRenewalTerms: c.autoRenewalTerms ?? '',
        cancellationNoticePeriodDays: c.cancellationNoticePeriodDays != null ? String(c.cancellationNoticePeriodDays) : '',
        contractValue: c.contractValue != null ? String(c.contractValue) : '',
        paymentTerms: c.paymentTerms ?? '',
        budgetCategory: c.budgetCategory ?? '',
        glCode: c.glCode ?? '',
        departmentScope: c.departmentScope,
        notes: c.notes ?? '',
        erpPoNumber: c.erpPoNumber ?? '',
        erpGlCode: c.erpGlCode ?? '',
      });
      setBudgetCategories(existingData.budgetCategories.map((bc) => bc.budgetCategory));
      setSiteNumbers(existingData.siteAssociations.map((sa) => sa.siteNumber));
      setAnnualCosts(
        existingData.annualCosts.map((ac) => ({
          fiscalYear: String(ac.fiscalYear),
          annualAmount: String(ac.annualAmount),
          notes: ac.notes ?? '',
        })),
      );
    }
  }, [existingData, isEdit]);

  // Handle AI extraction result — auto-populate form fields
  function handleAIExtracted(result: ContractSetupResult) {
    setAiResult(result);
    setAiAssistedCreation(true);
    confidence.resetConfirmations();

    const fields = result.extractedFields;
    const meta: AIFieldMeta[] = [];
    const updates: Partial<typeof form> = {};

    if (fields.contractTitle?.value) {
      updates.contractTitle = fields.contractTitle.value;
      meta.push({ fieldName: 'contractTitle', confidence: fields.contractTitle.confidence });
    }
    if (fields.effectiveDate?.value) {
      updates.effectiveDate = fields.effectiveDate.value;
      meta.push({ fieldName: 'effectiveDate', confidence: fields.effectiveDate.confidence });
    }
    if (fields.expirationDate?.value) {
      updates.expirationDate = fields.expirationDate.value;
      meta.push({ fieldName: 'expirationDate', confidence: fields.expirationDate.confidence });
    }
    if (fields.contractValue?.value != null) {
      updates.contractValue = String(fields.contractValue.value);
      meta.push({ fieldName: 'contractValue', confidence: fields.contractValue.confidence });
    }
    if (fields.paymentTerms?.value) {
      updates.paymentTerms = fields.paymentTerms.value;
      meta.push({ fieldName: 'paymentTerms', confidence: fields.paymentTerms.confidence });
    }
    if (fields.autoRenewalTerms?.value) {
      updates.autoRenewalTerms = fields.autoRenewalTerms.value;
      meta.push({ fieldName: 'autoRenewalTerms', confidence: fields.autoRenewalTerms.confidence });
    }

    // If vendor match found, try to select the matched vendor
    if (result.vendorMatch?.matchFound && result.vendorMatch.matchedVendorId) {
      updates.vendorId = String(result.vendorMatch.matchedVendorId);
      meta.push({ fieldName: 'vendorId', confidence: result.vendorMatch.similarity ?? 0.5 });
    }

    setForm((f) => ({ ...f, ...updates }));
    setAiFieldMeta(meta);
  }

  /** Get AI confidence for a field, or null if not AI-populated. */
  function getFieldConfidence(fieldName: string): number | null {
    const meta = aiFieldMeta.find((m) => m.fieldName === fieldName);
    return meta ? meta.confidence : null;
  }

  const createMutation = useMutation({
    mutationFn: (input: CreateContractInput) => contractService.create(input),
    onSuccess: (contract) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      navigate(`/contracts/${contract.contractId}`);
    },
    onError: (err: Error) => setSubmitError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateContractInput }) =>
      contractService.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', Number(editId)] });
      navigate(`/contracts/${editId}`);
    },
    onError: (err: Error) => setSubmitError(err.message),
  });

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!form.vendorId) newErrors.vendorId = 'Vendor is required.';
    if (!form.contractTitle.trim()) newErrors.contractTitle = 'Contract title is required.';
    if (!form.departmentScope.trim()) newErrors.departmentScope = 'Department scope is required.';
    if (form.glCode && !/^\d{3}-\d{4}$/.test(form.glCode)) {
      newErrors.glCode = 'GL code must follow XXX-XXXX format.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function buildInput(): CreateContractInput {
    return {
      vendorId: Number(form.vendorId),
      contractTitle: form.contractTitle.trim(),
      contractType: form.contractType.trim() || null,
      effectiveDate: form.effectiveDate || null,
      expirationDate: form.expirationDate || null,
      autoRenewalTerms: form.autoRenewalTerms.trim() || null,
      cancellationNoticePeriodDays: form.cancellationNoticePeriodDays ? Number(form.cancellationNoticePeriodDays) : null,
      contractValue: form.contractValue ? Number(form.contractValue) : null,
      paymentTerms: form.paymentTerms.trim() || null,
      budgetCategory: form.budgetCategory.trim() || null,
      glCode: form.glCode.trim() || null,
      departmentScope: form.departmentScope.trim(),
      notes: form.notes.trim() || null,
      erpPoNumber: form.erpPoNumber.trim() || null,
      erpGlCode: form.erpGlCode.trim() || null,
      budgetCategories: budgetCategories.length > 0 ? budgetCategories : undefined,
      annualCosts: annualCosts.length > 0
        ? annualCosts.map((ac) => ({
            fiscalYear: Number(ac.fiscalYear),
            annualAmount: Number(ac.annualAmount),
            notes: ac.notes.trim() || null,
          }))
        : undefined,
      siteNumbers: siteNumbers.length > 0 ? siteNumbers : undefined,
    };
  }

  function handleSubmit() {
    if (!validate()) return;
    setSubmitError(null);

    if (isEdit) {
      const c = existingData!.contract;
      updateMutation.mutate({
        id: Number(editId),
        input: {
          ...buildInput(),
          updatedAt: c.updatedAt,
        },
      });
    } else {
      const input = buildInput();
      // Set AI-assisted creation flag if extraction was used
      if (aiAssistedCreation) {
        (input as CreateContractInput & { aiAssistedCreation?: boolean }).aiAssistedCreation = true;
      }
      createMutation.mutate(input);
    }
  }

  function addBudgetCategory() {
    const cat = newCategory.trim();
    if (cat && !budgetCategories.includes(cat)) {
      setBudgetCategories([...budgetCategories, cat]);
      setNewCategory('');
    }
  }

  function removeBudgetCategory(cat: string) {
    setBudgetCategories(budgetCategories.filter((c) => c !== cat));
  }

  function addSiteNumber() {
    const site = newSite.trim();
    if (/^\d{3}$/.test(site) && !siteNumbers.includes(site)) {
      setSiteNumbers([...siteNumbers, site]);
      setNewSite('');
    }
  }

  function removeSiteNumber(site: string) {
    setSiteNumbers(siteNumbers.filter((s) => s !== site));
  }

  function addAnnualCost() {
    setAnnualCosts([...annualCosts, { fiscalYear: String(new Date().getFullYear()), annualAmount: '', notes: '' }]);
  }

  function removeAnnualCost(idx: number) {
    setAnnualCosts(annualCosts.filter((_, i) => i !== idx));
  }

  function updateAnnualCost(idx: number, field: string, value: string) {
    setAnnualCosts(annualCosts.map((ac, i) => (i === idx ? { ...ac, [field]: value } : ac)));
  }

  if (isEdit && loadingContract) {
    return <LoadingSpinner message="Loading contract..." />;
  }

  const isPending = createMutation.isPending || updateMutation.isPending;
  const vendors = vendorsData?.data ?? [];

  // Check if all red-flagged fields are confirmed before allowing save
  const hasUnconfirmedRedFields =
    aiFieldMeta.length > 0 && !confidence.allRedFieldsConfirmed(aiFieldMeta);

  /** Renders a field with confidence indicator if AI-populated, or a plain FormField otherwise. */
  function renderField(
    fieldName: string,
    label: string,
    value: string,
    onChange: (val: string) => void,
    opts?: { type?: string; placeholder?: string; error?: string; required?: boolean },
  ) {
    const conf = getFieldConfidence(fieldName);
    if (conf !== null) {
      return (
        <ConfidenceField
          label={label}
          value={value}
          confidence={conf}
          onChange={(e) => onChange(e.target.value)}
          confirmed={confidence.isFieldConfirmed(fieldName)}
          onConfirm={() => confidence.confirmField(fieldName)}
        />
      );
    }
    return (
      <FormField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        type={opts?.type}
        placeholder={opts?.placeholder}
        error={opts?.error}
        required={opts?.required}
      />
    );
  }

  return (
    <div style={{ maxWidth: '720px' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.5rem' }}>
        {isEdit ? 'Edit Contract' : 'New Contract'}
      </h1>

      {submitError && <ErrorMessage message={submitError} />}

      {/* AI-Assisted Upload — only on create, not edit */}
      {!isEdit && (
        <AIDocumentUpload
          documentType="contract"
          onExtracted={(result) => handleAIExtracted(result as ContractSetupResult)}
        />
      )}

      {/* Vendor match warning from AI extraction */}
      {aiResult?.vendorMatch?.matchFound && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          backgroundColor: '#fffbeb',
          border: '1px solid #fbbf24',
          borderRadius: '6px',
        }}>
          <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 500, color: '#92400e' }}>
            ⚠ A similar vendor was found: <strong>{aiResult.vendorMatch.matchedVendorName}</strong>
            {aiResult.vendorMatch.similarity != null && (
              <> (similarity: {Math.round(aiResult.vendorMatch.similarity * 100)}%)</>
            )}
          </p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#92400e' }}>
            The vendor dropdown has been pre-selected. Change it if this is not the correct vendor.
          </p>
        </div>
      )}

      {/* AI disclaimer */}
      {aiResult?.disclaimer && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.5rem 0.75rem',
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '4px',
          fontSize: '0.75rem',
          color: '#0369a1',
        }}>
          {aiResult.disclaimer}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        {/* Vendor */}
        <FormField label="Vendor" error={errors.vendorId}>
          <select
            value={form.vendorId}
            onChange={(e) => setForm((f) => ({ ...f, vendorId: e.target.value }))}
            style={{
              width: '100%',
              padding: '0.4rem 0.75rem',
              borderRadius: '4px',
              border: `1px solid ${errors.vendorId ? '#ef4444' : '#d1d5db'}`,
              fontSize: '0.875rem',
            }}
          >
            <option value="">Select vendor...</option>
            {vendors.map((v: Vendor) => (
              <option key={v.vendorId} value={v.vendorId}>{v.vendorName}</option>
            ))}
          </select>
        </FormField>

        {renderField(
          'contractTitle',
          'Contract Title',
          form.contractTitle,
          (val) => setForm((f) => ({ ...f, contractTitle: val })),
          { error: errors.contractTitle, required: true },
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
          <FormField
            label="Contract Type"
            value={form.contractType}
            onChange={(e) => setForm((f) => ({ ...f, contractType: e.target.value }))}
            placeholder="e.g., MSA, SOW, NDA"
          />
          <FormField
            label="Department Scope"
            value={form.departmentScope}
            onChange={(e) => setForm((f) => ({ ...f, departmentScope: e.target.value }))}
            error={errors.departmentScope}
            placeholder="e.g., IT, Facilities"
            required
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
          {renderField(
            'effectiveDate',
            'Effective Date',
            form.effectiveDate,
            (val) => setForm((f) => ({ ...f, effectiveDate: val })),
            { type: 'date' },
          )}
          {renderField(
            'expirationDate',
            'Expiration Date',
            form.expirationDate,
            (val) => setForm((f) => ({ ...f, expirationDate: val })),
            { type: 'date' },
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
          {renderField(
            'contractValue',
            'Contract Value ($)',
            form.contractValue,
            (val) => setForm((f) => ({ ...f, contractValue: val })),
            { type: 'number', placeholder: '0.00' },
          )}
          {renderField(
            'paymentTerms',
            'Payment Terms',
            form.paymentTerms,
            (val) => setForm((f) => ({ ...f, paymentTerms: val })),
            { placeholder: 'e.g., Net 30' },
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
          <FormField
            label="GL Code"
            value={form.glCode}
            onChange={(e) => setForm((f) => ({ ...f, glCode: e.target.value }))}
            error={errors.glCode}
            placeholder="XXX-XXXX"
          />
          <FormField
            label="Budget Category"
            value={form.budgetCategory}
            onChange={(e) => setForm((f) => ({ ...f, budgetCategory: e.target.value }))}
            placeholder="e.g., Telecom"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
          {renderField(
            'autoRenewalTerms',
            'Auto-Renewal Terms',
            form.autoRenewalTerms,
            (val) => setForm((f) => ({ ...f, autoRenewalTerms: val })),
            { placeholder: 'e.g., 12 months' },
          )}
          <FormField
            label="Cancellation Notice (days)"
            type="number"
            value={form.cancellationNoticePeriodDays}
            onChange={(e) => setForm((f) => ({ ...f, cancellationNoticePeriodDays: e.target.value }))}
            placeholder="e.g., 90"
          />
        </div>

        <FormField label="Notes">
          <textarea
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            rows={3}
            style={{
              width: '100%',
              padding: '0.4rem 0.75rem',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              fontSize: '0.875rem',
              resize: 'vertical',
            }}
          />
        </FormField>

        {/* Multi-select Budget Categories */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500, color: '#334155' }}>
            Additional Budget Categories
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Add category..."
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBudgetCategory(); } }}
              style={{ flex: 1, padding: '0.4rem 0.75rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
            />
            <button
              type="button"
              onClick={addBudgetCategory}
              style={{ padding: '0.4rem 0.75rem', borderRadius: '4px', border: '1px solid #d1d5db', backgroundColor: '#f8fafc', cursor: 'pointer', fontSize: '0.8rem' }}
            >
              Add
            </button>
          </div>
          {budgetCategories.length > 0 && (
            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
              {budgetCategories.map((cat) => (
                <span
                  key={cat}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.15rem 0.5rem',
                    borderRadius: '9999px',
                    backgroundColor: '#dbeafe',
                    color: '#1e40af',
                    fontSize: '0.75rem',
                  }}
                >
                  {cat}
                  <button
                    type="button"
                    onClick={() => removeBudgetCategory(cat)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1e40af', fontSize: '0.8rem', padding: 0 }}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Site Association Picker */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500, color: '#334155' }}>
            Site Associations
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input
              value={newSite}
              onChange={(e) => setNewSite(e.target.value)}
              placeholder="3-digit site #"
              maxLength={3}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSiteNumber(); } }}
              style={{ width: '120px', padding: '0.4rem 0.75rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
            />
            <button
              type="button"
              onClick={addSiteNumber}
              style={{ padding: '0.4rem 0.75rem', borderRadius: '4px', border: '1px solid #d1d5db', backgroundColor: '#f8fafc', cursor: 'pointer', fontSize: '0.8rem' }}
            >
              Add Site
            </button>
          </div>
          {siteNumbers.length > 0 && (
            <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
              {siteNumbers.map((site) => (
                <span
                  key={site}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.15rem 0.5rem',
                    borderRadius: '9999px',
                    backgroundColor: '#f1f5f9',
                    color: '#475569',
                    fontSize: '0.75rem',
                  }}
                >
                  Site {site}
                  <button
                    type="button"
                    onClick={() => removeSiteNumber(site)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', fontSize: '0.8rem', padding: 0 }}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Annual Costs */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#334155' }}>
              Annual Cost Breakdown
            </label>
            <button
              type="button"
              onClick={addAnnualCost}
              style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', backgroundColor: '#f8fafc', cursor: 'pointer', fontSize: '0.75rem' }}
            >
              + Add Year
            </button>
          </div>
          {annualCosts.map((ac, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
              <input
                type="number"
                value={ac.fiscalYear}
                onChange={(e) => updateAnnualCost(idx, 'fiscalYear', e.target.value)}
                placeholder="Year"
                style={{ width: '80px', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem' }}
              />
              <input
                type="number"
                value={ac.annualAmount}
                onChange={(e) => updateAnnualCost(idx, 'annualAmount', e.target.value)}
                placeholder="Amount"
                style={{ width: '120px', padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem' }}
              />
              <input
                value={ac.notes}
                onChange={(e) => updateAnnualCost(idx, 'notes', e.target.value)}
                placeholder="Notes"
                style={{ flex: 1, padding: '0.3rem 0.5rem', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.8rem' }}
              />
              <button
                type="button"
                onClick={() => removeAnnualCost(idx)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '1rem', padding: '0.25rem' }}
              >
                x
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
          <button
            type="submit"
            disabled={isPending || hasUnconfirmedRedFields}
            title={hasUnconfirmedRedFields ? 'Please confirm all red-flagged fields before saving' : undefined}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: hasUnconfirmedRedFields ? '#9ca3af' : '#c8102e',
              color: '#fff',
              cursor: isPending || hasUnconfirmedRedFields ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            {isPending ? 'Saving...' : isEdit ? 'Update Contract' : 'Create Contract'}
          </button>
          <button
            type="button"
            onClick={() => navigate(isEdit ? `/contracts/${editId}` : '/contracts')}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
